import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Loader2, Plus, ChevronDown, ChevronRight, Edit2, Search } from "lucide-react";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { STORAGE_KEY } from "../../lib/constants";

export default function CategoryManagementTab() {
	return (
		<Tabs defaultValue="categories">
			<TabsList className="mb-4 dark:bg-gray-800 dark:border-gray-700">
				<TabsTrigger value="categories">Categories (L1/L2)</TabsTrigger>
				<TabsTrigger value="tags">Tags</TabsTrigger>
			</TabsList>
			<TabsContent value="categories">
				<CategoriesSection />
			</TabsContent>
			<TabsContent value="tags">
				<TagsSection />
			</TabsContent>
		</Tabs>
	);
}

function CategoriesSection() {
	const [loading, setLoading] = useState(true);
	const [tree, setTree] = useState<any[]>([]);
	const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
	const [showCreate, setShowCreate] = useState(false);
	const [createParentId, setCreateParentId] = useState<string | undefined>(undefined);
	const [newName, setNewName] = useState("");
	const [isOtc, setIsOtc] = useState(false);
	const [isHealthCondition, setIsHealthCondition] = useState(false);
	const [showInHome, setShowInHome] = useState(true);
	const [showInMenu, setShowInMenu] = useState(true);
	const [saving, setSaving] = useState(false);
	const { toast } = useToast();

	const getUserInfo = () => {
		const user = localStorage.getItem(STORAGE_KEY);
		if (!user) return { email: "unknown@pillplus.com" };
		const parsed = JSON.parse(user);
		return { email: parsed.email || "unknown@pillplus.com", name: parsed.displayName || parsed.name };
	};

	useEffect(() => {
		fetchCategories();
	}, []);

	const fetchCategories = async () => {
		setLoading(true);
		try {
			const res = await api.b2cMgmtGetCategories(true);
			if (res?.success) {
				setTree(res.data?.tree || []);
			}
		} catch (error: any) {
			toast({ title: "Error", description: "Failed to load categories", variant: "destructive" });
		} finally {
			setLoading(false);
		}
	};

	const toggleExpand = (id: string) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const handleCreate = async () => {
		if (!newName.trim()) return;
		setSaving(true);
		const user = getUserInfo();
		try {
			const res = await api.b2cMgmtCreateCategory({
				name: newName.trim(),
				parent_id: createParentId,
				is_otc_category: isOtc,
				is_health_condition: isHealthCondition,
				show_in_home: showInHome,
				show_in_menu: showInMenu,
				user,
			});
			if (res?.success) {
				toast({ title: "Created", description: `Category "${newName}" created` });
				setShowCreate(false);
				setNewName("");
				setCreateParentId(undefined);
				setIsOtc(false);
				setIsHealthCondition(false);
				setShowInHome(true);
				setShowInMenu(true);
				fetchCategories();
			} else {
				toast({ title: "Error", description: res?.error, variant: "destructive" });
			}
		} catch (error: any) {
			toast({ title: "Error", description: "Failed to create", variant: "destructive" });
		} finally {
			setSaving(false);
		}
	};

	const handleToggleField = async (id: string, field: string, currentValue: boolean) => {
		try {
			const res = await api.b2cMgmtUpdateCategory(id, { [field]: !currentValue });
			if (res?.success) {
				toast({ title: "Updated" });
				fetchCategories();
			}
		} catch (error: any) {
			toast({ title: "Error", description: "Failed to update", variant: "destructive" });
		}
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center h-40">
				<Loader2 className="h-6 w-6 animate-spin text-blue-500" />
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<p className="text-sm text-gray-500 dark:text-gray-400">{tree.length} L1 categories</p>
				<Button size="sm" onClick={() => { setCreateParentId(undefined); setShowCreate(true); }}>
					<Plus className="h-4 w-4 mr-1" /> Add L1 Category
				</Button>
			</div>

			{/* Legend */}
			<div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
				<span>Toggles:</span>
				<span><strong>Home</strong> = Show on home screen</span>
				<span><strong>Menu</strong> = Show in All Categories</span>
				<span><strong>Active</strong> = Category in use</span>
			</div>

			<Card className="dark:bg-gray-800 dark:border-gray-700">
				<CardContent className="p-0">
					<div className="divide-y dark:divide-gray-700">
						{tree.map((l1: any) => (
							<div key={l1.id}>
								<div className="flex items-center gap-2 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
									<button onClick={() => toggleExpand(l1.id)} className="p-1">
										{l1.children?.length > 0 ? (
											expandedIds.has(l1.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
										) : (
											<div className="w-4" />
										)}
									</button>
									<div className="flex-1 min-w-0">
										<span className="font-medium dark:text-gray-200">{l1.name}</span>
										{l1.is_otc_category && <Badge variant="outline" className="text-xs ml-2">OTC</Badge>}
										{l1.is_health_condition && <Badge variant="outline" className="text-xs ml-2">Health</Badge>}
									</div>
									<Badge variant="secondary" className="text-xs shrink-0">{Number(l1.product_count) + (l1.children?.reduce((sum: number, c: any) => sum + Number(c.product_count || 0), 0) || 0)} products</Badge>
									<div className="flex items-center gap-1 shrink-0" title="Show in Home">
										<span className="text-[10px] text-gray-400">Home</span>
										<Switch checked={l1.show_in_home} onCheckedChange={() => handleToggleField(l1.id, "show_in_home", l1.show_in_home)} />
									</div>
									<div className="flex items-center gap-1 shrink-0" title="Show in Menu">
										<span className="text-[10px] text-gray-400">Menu</span>
										<Switch checked={l1.show_in_menu} onCheckedChange={() => handleToggleField(l1.id, "show_in_menu", l1.show_in_menu)} />
									</div>
									<div className="flex items-center gap-1 shrink-0" title="Active">
										<span className="text-[10px] text-gray-400">Active</span>
										<Switch checked={l1.is_active} onCheckedChange={() => handleToggleField(l1.id, "is_active", l1.is_active)} />
									</div>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => { setCreateParentId(l1.id); setShowCreate(true); }}
										title="Add L2 subcategory"
									>
										<Plus className="h-3 w-3" />
									</Button>
								</div>
								{expandedIds.has(l1.id) && l1.children?.length > 0 && (
									<div className="bg-gray-50 dark:bg-gray-900/50">
										{l1.children.map((l2: any) => (
											<div key={l2.id} className="flex items-center gap-2 p-3 pl-12 border-t dark:border-gray-700/50">
												<div className="flex-1 min-w-0">
													<span className="dark:text-gray-300">{l2.name}</span>
													{l2.is_otc_category && <Badge variant="outline" className="text-xs ml-2">OTC</Badge>}
													{l2.is_health_condition && <Badge variant="outline" className="text-xs ml-2">Health</Badge>}
												</div>
												<Badge variant="secondary" className="text-xs shrink-0">{l2.product_count} products</Badge>
												<div className="flex items-center gap-1 shrink-0" title="Show in Home">
													<span className="text-[10px] text-gray-400">Home</span>
													<Switch checked={l2.show_in_home} onCheckedChange={() => handleToggleField(l2.id, "show_in_home", l2.show_in_home)} />
												</div>
												<div className="flex items-center gap-1 shrink-0" title="Show in Menu">
													<span className="text-[10px] text-gray-400">Menu</span>
													<Switch checked={l2.show_in_menu} onCheckedChange={() => handleToggleField(l2.id, "show_in_menu", l2.show_in_menu)} />
												</div>
												<div className="flex items-center gap-1 shrink-0" title="Active">
													<span className="text-[10px] text-gray-400">Active</span>
													<Switch checked={l2.is_active} onCheckedChange={() => handleToggleField(l2.id, "is_active", l2.is_active)} />
												</div>
											</div>
										))}
									</div>
								)}
							</div>
						))}
					</div>
				</CardContent>
			</Card>

			{/* Create Dialog */}
			<Dialog open={showCreate} onOpenChange={setShowCreate}>
				<DialogContent className="dark:bg-gray-800 dark:border-gray-700">
					<DialogHeader>
						<DialogTitle className="dark:text-white">
							{createParentId ? "Add L2 Subcategory" : "Add L1 Category"}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<div>
							<Label className="dark:text-gray-300">Name</Label>
							<Input
								value={newName}
								onChange={(e) => setNewName(e.target.value)}
								placeholder="Category name"
								className="dark:bg-gray-700 dark:border-gray-600"
							/>
						</div>
						<div className="flex flex-wrap items-center gap-4">
							<div className="flex items-center gap-2">
								<Switch checked={isOtc} onCheckedChange={setIsOtc} />
								<Label className="dark:text-gray-300">OTC Category</Label>
							</div>
							<div className="flex items-center gap-2">
								<Switch checked={isHealthCondition} onCheckedChange={setIsHealthCondition} />
								<Label className="dark:text-gray-300">Health Condition</Label>
							</div>
						</div>
						<div className="flex flex-wrap items-center gap-4">
							<div className="flex items-center gap-2">
								<Switch checked={showInHome} onCheckedChange={setShowInHome} />
								<Label className="dark:text-gray-300">Show in Home</Label>
							</div>
							<div className="flex items-center gap-2">
								<Switch checked={showInMenu} onCheckedChange={setShowInMenu} />
								<Label className="dark:text-gray-300">Show in Menu</Label>
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
						<Button onClick={handleCreate} disabled={saving || !newName.trim()}>
							{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
							Create
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}

function TagsSection() {
	const [loading, setLoading] = useState(true);
	const [tags, setTags] = useState<any[]>([]);
	const [search, setSearch] = useState("");
	const [showCreate, setShowCreate] = useState(false);
	const [newTagName, setNewTagName] = useState("");
	const [saving, setSaving] = useState(false);
	const { toast } = useToast();

	const getUserInfo = () => {
		const user = localStorage.getItem(STORAGE_KEY);
		if (!user) return { email: "unknown@pillplus.com" };
		const parsed = JSON.parse(user);
		return { email: parsed.email || "unknown@pillplus.com", name: parsed.displayName || parsed.name };
	};

	useEffect(() => {
		fetchTags();
	}, [search]);

	const fetchTags = async () => {
		setLoading(true);
		try {
			const res = await api.b2cMgmtGetTags(search, true);
			if (res?.success) {
				setTags(res.data || []);
			}
		} catch (error: any) {
			toast({ title: "Error", description: "Failed to load tags", variant: "destructive" });
		} finally {
			setLoading(false);
		}
	};

	const handleCreateTag = async () => {
		if (!newTagName.trim()) return;
		setSaving(true);
		const user = getUserInfo();
		try {
			const res = await api.b2cMgmtCreateTag({ tag_name: newTagName.trim(), user });
			if (res?.success) {
				toast({ title: "Created", description: `Tag "${newTagName}" created` });
				setShowCreate(false);
				setNewTagName("");
				fetchTags();
			} else {
				toast({ title: "Error", description: res?.error, variant: "destructive" });
			}
		} catch (error: any) {
			toast({ title: "Error", description: "Failed to create tag", variant: "destructive" });
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-3">
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
					<Input
						placeholder="Search tags..."
						className="pl-9 dark:bg-gray-700 dark:border-gray-600"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>
				<Button size="sm" onClick={() => setShowCreate(true)}>
					<Plus className="h-4 w-4 mr-1" /> Add Tag
				</Button>
			</div>

			<Card className="dark:bg-gray-800 dark:border-gray-700">
				<CardContent className="p-0">
					{loading ? (
						<div className="flex items-center justify-center h-40">
							<Loader2 className="h-6 w-6 animate-spin text-blue-500" />
						</div>
					) : tags.length === 0 ? (
						<div className="text-center py-12 text-gray-500 dark:text-gray-400">No tags found</div>
					) : (
						<div className="overflow-x-auto">
							<table className="w-full text-sm">
								<thead>
									<tr className="border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
										<th className="py-3 px-4 text-left dark:text-gray-300">Tag Name</th>
										<th className="py-3 px-4 text-left dark:text-gray-300">Slug</th>
										<th className="py-3 px-4 text-left dark:text-gray-300">Type</th>
										<th className="py-3 px-4 text-right dark:text-gray-300">Products</th>
										<th className="py-3 px-4 text-center dark:text-gray-300">Active</th>
									</tr>
								</thead>
								<tbody>
									{tags.map((tag: any) => (
										<tr key={tag.tag_id} className="border-b dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/50">
											<td className="py-2 px-4 font-medium dark:text-gray-200">{tag.tag_name}</td>
											<td className="py-2 px-4 text-xs font-mono dark:text-gray-400">{tag.tag_slug}</td>
											<td className="py-2 px-4">
												<Badge variant="outline" className="text-xs">{tag.tag_type}</Badge>
											</td>
											<td className="py-2 px-4 text-right dark:text-gray-300">{tag.product_count}</td>
											<td className="py-2 px-4 text-center">
												{tag.is_active ? (
													<Badge variant="default" className="text-xs">Active</Badge>
												) : (
													<Badge variant="secondary" className="text-xs">Inactive</Badge>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Create Tag Dialog */}
			<Dialog open={showCreate} onOpenChange={setShowCreate}>
				<DialogContent className="dark:bg-gray-800 dark:border-gray-700">
					<DialogHeader>
						<DialogTitle className="dark:text-white">Create Tag</DialogTitle>
					</DialogHeader>
					<div>
						<Label className="dark:text-gray-300">Tag Name</Label>
						<Input
							value={newTagName}
							onChange={(e) => setNewTagName(e.target.value)}
							placeholder="Enter tag name"
							className="dark:bg-gray-700 dark:border-gray-600"
						/>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
						<Button onClick={handleCreateTag} disabled={saving || !newTagName.trim()}>
							{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
							Create
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
